import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { ExcluirFinanceiroVendaRequest } from '@shared/services/requests/vendas/excluir-financeiro-venda.request';
import { ContasReceberVendaLancadaStruct } from '@shared/services/structs/financas/contas-receber-venda-lancada.struct';
import { MovimentacaoVendaLancadaStruct } from '@shared/services/structs/financas/movimentacao-venda-lancada.struct';

import { PvFinanceiroLancamentosComponent } from '../../components/pv-financeiro-lancamentos/pv-financeiro-lancamentos.component';

export interface PvExcluirFinanceiroDialogData {
    idVenda: number;
    /** "Pedido nº 123" — o rótulo do cabeçalho. */
    rotuloDocumento: string;
}

/**
 * Excluir financeiro do pedido (T08, DF7): lista o que o Finanças tem do pedido com a ação prevista por item
 * (excluir, cancelar cobrança e parcela) ou o motivo que impede — qualquer bloqueio recusa a exclusão inteira,
 * então o Confirmar fica desabilitado. Fecha com `true` quando excluiu.
 */
@Component({
    selector: 'app-pv-excluir-financeiro-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        AlertCardComponent,
        LoadingBoxComponent,
        PvFinanceiroLancamentosComponent,
    ],
    templateUrl: './pv-excluir-financeiro-dialog.component.html',
    styleUrls: ['./pv-excluir-financeiro-dialog.component.css'],
})
export class PvExcluirFinanceiroDialogComponent implements OnInit {
    public isLoading = true;
    public isExcluindo = false;
    public contas: ContasReceberVendaLancadaStruct[] = [];
    public movimentacoes: MovimentacaoVendaLancadaStruct[] = [];
    public justificativa = '';

    constructor(
        public dialogRef: MatDialogRef<PvExcluirFinanceiroDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: PvExcluirFinanceiroDialogData,
        private processoVendaService: ProcessoVendaService,
        private alertService: AlertService,
    ) {}

    /** Sem o que o Finanças tem do pedido não há o que mostrar nem confirmar: avisa por toast e fecha. */
    public ngOnInit(): void {
        this.processoVendaService.getFinanceiro(this.data.idVenda).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível consultar o financeiro do pedido.', AlertType.warning);
                    this.dialogRef.close(false);
                    return;
                }

                this.contas = res.contas ?? [];
                this.movimentacoes = res.movimentacoes ?? [];
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Aviso', 'Não foi possível consultar o financeiro do pedido agora. Tente novamente em alguns instantes.', AlertType.warning);
                this.dialogRef.close(false);
            },
        });
    }

    public get temFinanceiro(): boolean {
        return this.contas.length > 0 || this.movimentacoes.length > 0;
    }

    /** Qualquer item bloqueado recusa a exclusão inteira (tudo ou nada). */
    public get bloqueios(): string[] {
        const parcelas = this.contas.flatMap((c) => c.parcelas).map((p) => p.motivoBloqueioExclusao);
        const movimentacoes = this.movimentacoes.map((m) => m.motivoBloqueioExclusao);

        return [...parcelas, ...movimentacoes].filter((m): m is string => !!m);
    }

    /** Sem nada no Finanças, confirmar só atualiza o registro do pedido (o orquestrador registra a exclusão que faltou). */
    public get podeExcluir(): boolean {
        return !this.isLoading && this.bloqueios.length === 0;
    }

    public get tooltipExcluir(): string {
        if (this.isLoading || this.podeExcluir) return '';
        return 'A exclusão está bloqueada: resolva os itens indicados no Finanças e tente de novo.';
    }

    public confirmar(): void {
        if (!this.podeExcluir || this.isExcluindo) return;

        this.isExcluindo = true;
        const request = new ExcluirFinanceiroVendaRequest();
        request.justificativa = this.justificativa.trim() || null;

        this.processoVendaService.excluirFinanceiro(this.data.idVenda, request).subscribe({
            next: (res) => {
                this.isExcluindo = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível excluir o financeiro do pedido.', AlertType.warning);
                    return;
                }

                // Excluiu: o aviso (cobrança não cancelada, registro no pedido pendente) vai no toast de aviso.
                if (res.avisoDescricao) {
                    this.alertService.show('Aviso', res.avisoDescricao, AlertType.warning);
                } else {
                    this.alertService.show('Financeiro excluído', 'O pedido voltou a aceitar edição.', AlertType.success);
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isExcluindo = false;
                this.alertService.show('Aviso', 'Não foi possível excluir o financeiro agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close(false);
    }
}
